import { useTranslation } from "react-i18next";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useCompanyGetAssetStatsQuery } from "@/services/(company)/api.assets";
import {
  CompositionBar,
  MetricValue,
  MiniMetric,
  MiniStat,
  ProgressTrack,
  WidgetNoAccess,
} from "./primitives";

/** Asset requests waiting, plus how much of the inventory is actually free. */
export const AssetRequestsWidget = ({ size }: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  const { data, isLoading, error } = useCompanyGetAssetStatsQuery();

  if (error) return <WidgetNoAccess error={error} />;

  const stats = data?.data;
  const pending = stats?.tabCounts?.pendingAssetRequests ?? 0;

  // The card draws the chip and the title at every size, this one included, so
  // there is nothing here to add beyond the number.
  if (size === "xs") {
    return <MiniMetric value={pending} isLoading={isLoading} />;
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-0.5">
        <MetricValue value={pending} isLoading={isLoading} />
        <p className="text-[11px] text-muted-foreground">
          {t("company:widgets.pending_requests")}
        </p>
      </div>
    );
  }

  // Same width as `sm`, stood up: the bar `md` draws sideways keeps its width,
  // and the breakdown underneath it stacks instead of running 3 across.
  if (size === "tall") {
    return (
      <div className="flex h-full flex-col justify-between gap-2">
        <div>
          <MetricValue value={pending} isLoading={isLoading} />
          <p className="text-[11px] text-muted-foreground">
            {t("company:widgets.pending_requests")}
          </p>
        </div>
        <div className="flex flex-col gap-2 border-t border-border pt-2">
          <CompositionBar
            isLoading={isLoading}
            segments={[
              {
                label: t("company:widgets.in_use"),
                value: stats?.inUseAssets ?? 0,
                tone: "primary",
              },
              {
                label: t("company:widgets.available"),
                value: stats?.availableAssets ?? 0,
                tone: "success",
              },
              {
                label: t("company:widgets.maintenance"),
                value: stats?.maintenanceAssets ?? 0,
                tone: "warning",
              },
            ]}
          />
          <div className="flex flex-col gap-1.5">
            <MiniStat
              label={t("company:widgets.in_use")}
              tone="primary"
              value={stats?.inUseAssets ?? 0}
              isLoading={isLoading}
            />
            <MiniStat
              label={t("company:widgets.available")}
              tone="success"
              value={stats?.availableAssets ?? 0}
              isLoading={isLoading}
            />
            <MiniStat
              label={t("company:widgets.maintenance")}
              tone="warning"
              value={stats?.maintenanceAssets ?? 0}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-between gap-2">
      <div className="flex items-end gap-3">
        <MetricValue value={pending} isLoading={isLoading} scale="xl" />
        <p className="pb-1 text-[11px] text-muted-foreground">
          {t("company:widgets.pending_requests")}
        </p>
      </div>

      <div>
        <CompositionBar
          isLoading={isLoading}
          segments={[
            {
              label: t("company:widgets.in_use"),
              value: stats?.inUseAssets ?? 0,
              tone: "primary",
            },
            {
              label: t("company:widgets.available"),
              value: stats?.availableAssets ?? 0,
              tone: "success",
            },
            {
              label: t("company:widgets.maintenance"),
              value: stats?.maintenanceAssets ?? 0,
              tone: "warning",
            },
          ]}
        />
        <div className="mt-2 grid grid-cols-3 gap-2">
          <MiniStat
            label={t("company:widgets.in_use")}
            tone="primary"
            value={stats?.inUseAssets ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:widgets.available")}
            tone="success"
            value={stats?.availableAssets ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:widgets.maintenance")}
            tone="warning"
            value={stats?.maintenanceAssets ?? 0}
            isLoading={isLoading}
          />
        </div>
      </div>

      {size === "lg" && (
        <div className="border-t border-border pt-2">
          {/* "Total assets tracked" as a bare figure answered nothing a reader
              could act on. How much of that total is actually free is the
              question behind an asset-request queue, and available is a genuine
              subset of the total, so the fraction is honest rather than two
              unrelated numbers pushed into one sentence. */}
          <ProgressTrack
            label={t("company:widgets.available_to_assign")}
            value={stats?.availableAssets ?? 0}
            total={stats?.totalAssets ?? 0}
            tone="success"
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};
